import React, { useState, useEffect, useCallback, useRef } from "react";
import { Tooltip, CircularProgress } from "@mui/material";
import {
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarDaysIcon,
  DocumentArrowDownIcon,
} from "@heroicons/react/24/outline";
import moment from "moment";
import AxiosInstance from "../../../utils/axiosInstance";
import NoRows from "../../../components/DataGrid/animations/NoRows";

// ── TYPES ──────────────────────────────────────────────────────────────────────

interface PaginationStats {
  totalRecords: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
}

interface PayrollDataLoad {
  id: number;
  fileName: string;
  createdOn: string;
  createdBy: string;
  rowCount?: number;
  totalAmount?: number;
}

export interface PayrollImportDetail {
  id: number;
  payrollDataLoadId: number;
  co?: string;
  sub?: string;
  job?: string;
  acct?: string;
  employeeName?: string;
  employeeNo?: string;
  glCode?: string;
  code?: string;
  codeDescription?: string;
  debitAmount: number;
  creditAmount: number;
  payDate?: string;
  paymentReference?: string;
  notes?: string;
  isActive: boolean;
  isCorrection: boolean;
  comment?: string;
  isPaymentReversal: boolean;
  employeeNumber: number;
  jobId?: number;
  bonusTypeId: number;
  createdOn: string;
  createdBy: string;
  projectNumber: string;
  jobNumber?: string;
  cm?: string;
  bonusAmount: number;
  paidDate?: string;
  xBonusTypeId: number;
  bonusType: string;
}

export const defaultPayrollDataLoadDetail: Partial<PayrollImportDetail> = {
  isActive: true,
  isCorrection: false,
  isPaymentReversal: false,
  bonusTypeId: 0,
  debitAmount: 0,
  creditAmount: 0,
};

export type BonusType = "Stage5" | "Closing";

interface PayrollJob {
  jobId?: number;
  jobNumber?: string;
  bonusTypeId?: number;
  bonusType?: string;
  totalDebit?: number;
  totalCredit?: number;
  payDate?: string;
  hasNegativeBonus?: boolean;
  builderCount?: number;
}

interface PayrollBuilder {
  employeeNumber?: string;
  employeeName?: string;
  jobId?: number;
  jobNumber?: string;
  bonusTypeId?: number;
  bonusType?: string;
  totalDebit?: number;
  totalCredit?: number;
  payDate?: string;
  hasNegativeBonus?: boolean;
}

interface ImportFilesResponse {
  files: PayrollDataLoad[];
  paginationStats: PaginationStats;
}

interface DetailsResponse {
  details: PayrollImportDetail[];
  paginationStats: PaginationStats;
}

interface JobsResponse {
  jobs: PayrollJob[];
  paginationStats: PaginationStats;
}

interface BuildersResponse {
  builders: PayrollBuilder[];
  paginationStats: PaginationStats;
}

type DetailTab = "details" | "jobs" | "builders";

// ── HELPERS ────────────────────────────────────────────────────────────────────

const fmt = (n?: number) =>
  (n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2 });

const PAGE_SIZE = 10;

/** Infer bonus type from filename */
function inferBonusType(fileName: string): "Closing" | "Stage5" | null {
  const l = fileName.toLowerCase();
  if (l.includes("closing")) return "Closing";
  if (l.includes("stage5") || l.includes("stg5") || l.includes("stage 5")) return "Stage5";
  return null;
}

// ── PAGINATION ─────────────────────────────────────────────────────────────────

function Pagination({
  stats,
  onPageChange,
}: {
  stats: PaginationStats;
  onPageChange: (page: number) => void;
}) {
  if (!stats || stats.totalPages <= 1) return null;
  const { currentPage, totalPages, totalRecords, pageSize } = stats;
  const from = (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, totalRecords);

  return (
    <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50">
      <span className="text-xs text-gray-500">
        Showing {from}–{to} of {totalRecords} records
      </span>
      <div className="flex items-center gap-1">
        <button
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          className="p-1.5 rounded border border-gray-300 text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ChevronLeftIcon className="w-3.5 h-3.5" />
        </button>
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          let p: number;
          if (totalPages <= 5) p = i + 1;
          else if (currentPage <= 3) p = i + 1;
          else if (currentPage >= totalPages - 2) p = totalPages - 4 + i;
          else p = currentPage - 2 + i;
          return (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              className={`w-7 h-7 rounded border text-xs font-medium transition-colors ${
                p === currentPage
                  ? "bg-primary-600 text-white border-primary-600"
                  : "border-gray-300 text-gray-600 hover:bg-gray-100"
              }`}
            >
              {p}
            </button>
          );
        })}
        <button
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
          className="p-1.5 rounded border border-gray-300 text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ChevronRightIcon className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ── DETAIL PANEL ───────────────────────────────────────────────────────────────

function DetailPanel({
  fileId,
  fileName,
  onClose,
}: {
  fileId: number;
  fileName: string;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<DetailTab>("details");

  const [details, setDetails] = useState<PayrollImportDetail[]>([]);
  const [detailsPage, setDetailsPage] = useState(1);
  const [detailsStats, setDetailsStats] = useState<PaginationStats | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailSearch, setDetailSearch] = useState("");
  const [detailFlag, setDetailFlag] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [jobs, setJobs] = useState<PayrollJob[]>([]);
  const [jobsPage, setJobsPage] = useState(1);
  const [jobsStats, setJobsStats] = useState<PaginationStats | null>(null);
  const [jobsLoading, setJobsLoading] = useState(false);

  const [builders, setBuilders] = useState<PayrollBuilder[]>([]);
  const [buildersPage, setBuildersPage] = useState(1);
  const [buildersStats, setBuildersStats] = useState<PaginationStats | null>(null);
  const [buildersLoading, setBuildersLoading] = useState(false);

  const handleSearchChange = (val: string) => {
    setDetailSearch(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setDebouncedSearch(val); setDetailsPage(1); }, 350);
  };

  const fetchDetails = useCallback(async () => {
    setDetailsLoading(true);
    try {
      const params: Record<string, string | number | boolean> = { page: detailsPage, pageSize: PAGE_SIZE };
      if (debouncedSearch) params.employeeName = debouncedSearch;
      if (detailFlag === "correction") params.isCorrection = true;
      if (detailFlag === "reversal") params.isPaymentReversal = true;
      const res = await AxiosInstance.get<DetailsResponse>(`/api/payroll/import/files/${fileId}/details`, { params });
      setDetails(res.data?.details ?? []);
      setDetailsStats(res.data?.paginationStats ?? null);
    } catch (err) { console.error(err); setDetails([]); }
    finally { setDetailsLoading(false); }
  }, [fileId, detailsPage, debouncedSearch, detailFlag]);

  const fetchJobs = useCallback(async () => {
    setJobsLoading(true);
    try {
      const res = await AxiosInstance.get<JobsResponse>(`/api/payroll/import/files/${fileId}/jobs`, { params: { page: jobsPage, pageSize: PAGE_SIZE } });
      setJobs(res.data?.jobs ?? []);
      setJobsStats(res.data?.paginationStats ?? null);
    } catch (err) { console.error(err); setJobs([]); }
    finally { setJobsLoading(false); }
  }, [fileId, jobsPage]);

  const fetchBuilders = useCallback(async () => {
    setBuildersLoading(true);
    try {
      const res = await AxiosInstance.get<BuildersResponse>(`/api/payroll/import/files/${fileId}/builders`, { params: { page: buildersPage, pageSize: PAGE_SIZE } });
      setBuilders(res.data?.builders ?? []);
      setBuildersStats(res.data?.paginationStats ?? null);
    } catch (err) { console.error(err); setBuilders([]); }
    finally { setBuildersLoading(false); }
  }, [fileId, buildersPage]);

  useEffect(() => { if (tab === "details") fetchDetails(); }, [fetchDetails, tab]);
  useEffect(() => { if (tab === "jobs") fetchJobs(); }, [fetchJobs, tab]);
  useEffect(() => { if (tab === "builders") fetchBuilders(); }, [fetchBuilders, tab]);

  const tabs: { key: DetailTab; label: string }[] = [
    { key: "details", label: "Line Details" },
    { key: "jobs", label: "Jobs" },
    { key: "builders", label: "Builders" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" style={{ background: "rgba(0,0,0,0.45)", backdropFilter: "blur(3px)" }} onClick={onClose}>
      <div className="w-full sm:w-[min(1150px,94vw)] flex flex-col bg-white border-l border-gray-200 overflow-hidden shadow-2xl" style={{ animation: "slideIn 0.18s ease" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <h2 className="font-mono font-semibold text-sm text-gray-900 truncate">{fileName}</h2>
            <p className="text-xs text-gray-500 mt-0.5">Payroll Import — File #{fileId}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded border border-gray-300 text-gray-500 hover:bg-gray-100"><XMarkIcon className="w-4 h-4" /></button>
        </div>
        <div className="flex border-b border-gray-200 bg-white flex-shrink-0 px-5">
          {tabs.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-colors -mb-px ${tab === t.key ? "border-primary-600 text-primary-600" : "border-transparent text-gray-500 hover:text-gray-900"}`}>{t.label}</button>
          ))}
        </div>

        {tab === "details" && (
          <>
            <div className="flex flex-wrap items-center gap-2 px-5 py-2.5 border-b border-gray-200 bg-white flex-shrink-0">
              <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-gray-300 text-xs w-52">
                <MagnifyingGlassIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <input type="text" placeholder="Search employee…" value={detailSearch} onChange={(e) => handleSearchChange(e.target.value)} className="bg-transparent outline-none flex-1 min-w-0 text-gray-900 placeholder-gray-400" />
              </div>
              <select value={detailFlag} onChange={(e) => { setDetailFlag(e.target.value); setDetailsPage(1); }} className="px-2.5 py-1.5 rounded-md border border-gray-300 text-xs text-gray-600 outline-none bg-white">
                <option value="">All Rows</option>
                <option value="correction">Corrections Only</option>
                <option value="reversal">Reversals Only</option>
              </select>
              {detailsStats && <span className="ml-auto text-xs text-gray-500">{detailsStats.totalRecords} records</span>}
            </div>
            <div className="flex-1 overflow-auto">
              {detailsLoading ? <div className="flex items-center justify-center h-48"><CircularProgress size={28} /></div>
                : details.length === 0 ? <div className="flex items-center justify-center h-48"><NoRows /></div>
                : (
                  <table className="min-w-full divide-y divide-gray-200 table-fixed">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>{["#", "CO", "Job #", "Sub", "Employee", "Emp No.", "GL", "Code", "Debit", "Credit", "Pay Date", "Ref", "Flags"].map((h, i) => (
                        <th key={h} className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap ${i === 8 || i === 9 ? "text-right" : ""}`}>{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {details.map((r, i) => (
                        <tr key={r.id} className={`hover:bg-gray-50 text-sm ${r.isPaymentReversal ? "bg-red-50" : r.isCorrection ? "bg-amber-50" : ""}`}>
                          <td className="px-4 py-2.5 text-xs text-gray-400 font-mono">{(detailsPage - 1) * PAGE_SIZE + i + 1}</td>
                          <td className="px-4 py-2.5 text-xs text-gray-600 font-mono">{r.co ?? "—"}</td>
                          <td className="px-4 py-2.5 text-xs text-gray-900 font-mono font-medium">{r.jobNumber ?? "—"}</td>
                          <td className="px-4 py-2.5 text-xs text-gray-400 font-mono">{r.sub ?? "—"}</td>
                          <td className="px-4 py-2.5 text-xs text-gray-900">{r.employeeName ?? "—"}</td>
                          <td className="px-4 py-2.5 text-xs text-primary-600 font-mono">{r.employeeNumber ?? "—"}</td>
                          <td className="px-4 py-2.5 text-xs text-gray-400 font-mono">{r.glCode ?? "—"}</td>
                          <td className="px-4 py-2.5 text-xs"><span className="text-gray-600">{r.code}</span>{r.codeDescription && <span className="text-gray-400 ml-1">{r.codeDescription}</span>}</td>
                          <td className={`px-4 py-2.5 text-xs font-mono text-right ${r.debitAmount === 0 ? "text-red-500" : "text-gray-900"}`}>{r.debitAmount > 0 ? `$${fmt(r.debitAmount)}` : "—"}</td>
                          <td className={`px-4 py-2.5 text-xs font-mono text-right ${r.creditAmount > 0 ? "text-red-500" : "text-gray-400"}`}>{r.creditAmount > 0 ? `$${fmt(r.creditAmount)}` : "—"}</td>
                          <td className="px-4 py-2.5 text-xs text-gray-400 font-mono whitespace-nowrap">{r.payDate ? moment(r.payDate).format("MM/DD/YYYY") : "—"}</td>
                          <td className="px-4 py-2.5 text-xs text-gray-400 font-mono">{r.paymentReference ?? "—"}</td>
                          <td className="px-4 py-2.5 whitespace-nowrap">
                            {r.isCorrection && <span className="inline-block mr-1 px-1.5 py-0.5 rounded text-[9.5px] font-bold bg-amber-100 text-amber-700 uppercase">CORR</span>}
                            {r.isPaymentReversal && <span className="inline-block px-1.5 py-0.5 rounded text-[9.5px] font-bold bg-red-100 text-red-600 uppercase">REV</span>}
                            {!r.isCorrection && !r.isPaymentReversal && <span className="text-gray-300">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
            </div>
            {detailsStats && <Pagination stats={detailsStats} onPageChange={setDetailsPage} />}
          </>
        )}

        {tab === "jobs" && (
          <>
            <div className="flex-1 overflow-auto">
              {jobsLoading ? <div className="flex items-center justify-center h-48"><CircularProgress size={28} /></div>
                : jobs.length === 0 ? <div className="flex items-center justify-center h-48"><NoRows /></div>
                : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>{["#", "Job #", "Bonus Type", "Pay Date", "Total Debit", "Total Credit", "Builders", "Negative Bonus"].map((h, i) => (
                        <th key={h} className={`px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${i === 4 || i === 5 ? "text-right" : ""}`}>{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {jobs.map((j, i) => (
                        <tr key={i} className="hover:bg-gray-50 text-sm">
                          <td className="px-5 py-3 text-xs text-gray-400 font-mono">{(jobsPage - 1) * PAGE_SIZE + i + 1}</td>
                          <td className="px-5 py-3 text-xs text-gray-900 font-mono font-medium">{j.jobNumber ?? "—"}</td>
                          <td className="px-5 py-3"><span className={`inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-semibold uppercase tracking-wide ${j.bonusType === "Closing" ? "bg-purple-100 text-purple-700" : "bg-emerald-100 text-emerald-700"}`}>{j.bonusType ?? "—"}</span></td>
                          <td className="px-5 py-3 text-xs text-gray-500 font-mono">{j.payDate ? moment(j.payDate).format("MM/DD/YYYY") : "—"}</td>
                          <td className="px-5 py-3 text-xs font-mono text-right text-gray-900">{(j.totalDebit ?? 0) > 0 ? `$${fmt(j.totalDebit)}` : "—"}</td>
                          <td className={`px-5 py-3 text-xs font-mono text-right ${(j.totalCredit ?? 0) > 0 ? "text-red-500" : "text-gray-400"}`}>{(j.totalCredit ?? 0) > 0 ? `$${fmt(j.totalCredit)}` : "—"}</td>
                          <td className="px-5 py-3 text-xs text-gray-600">{j.builderCount ?? "—"}</td>
                          <td className="px-5 py-3">{j.hasNegativeBonus ? <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-600">Yes</span> : <span className="text-gray-300 text-xs">—</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
            </div>
            {jobsStats && <Pagination stats={jobsStats} onPageChange={setJobsPage} />}
          </>
        )}

        {tab === "builders" && (
          <>
            <div className="flex-1 overflow-auto">
              {buildersLoading ? <div className="flex items-center justify-center h-48"><CircularProgress size={28} /></div>
                : builders.length === 0 ? <div className="flex items-center justify-center h-48"><NoRows /></div>
                : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>{["#", "Employee", "Emp No.", "Job #", "Bonus Type", "Pay Date", "Total Debit", "Total Credit", "Negative"].map((h, i) => (
                        <th key={h} className={`px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${i === 6 || i === 7 ? "text-right" : ""}`}>{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {builders.map((b, i) => (
                        <tr key={i} className="hover:bg-gray-50 text-sm">
                          <td className="px-5 py-3 text-xs text-gray-400 font-mono">{(buildersPage - 1) * PAGE_SIZE + i + 1}</td>
                          <td className="px-5 py-3 text-xs text-gray-900">{b.employeeName ?? "—"}</td>
                          <td className="px-5 py-3 text-xs text-primary-600 font-mono">{b.employeeNumber ?? "—"}</td>
                          <td className="px-5 py-3 text-xs text-gray-900 font-mono">{b.jobNumber ?? "—"}</td>
                          <td className="px-5 py-3"><span className={`inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-semibold uppercase tracking-wide ${b.bonusType === "Closing" ? "bg-purple-100 text-purple-700" : "bg-emerald-100 text-emerald-700"}`}>{b.bonusType ?? "—"}</span></td>
                          <td className="px-5 py-3 text-xs text-gray-500 font-mono">{b.payDate ? moment(b.payDate).format("MM/DD/YYYY") : "—"}</td>
                          <td className="px-5 py-3 text-xs font-mono text-right text-gray-900">{(b.totalDebit ?? 0) > 0 ? `$${fmt(b.totalDebit)}` : "—"}</td>
                          <td className={`px-5 py-3 text-xs font-mono text-right ${(b.totalCredit ?? 0) > 0 ? "text-red-500" : "text-gray-400"}`}>{(b.totalCredit ?? 0) > 0 ? `$${fmt(b.totalCredit)}` : "—"}</td>
                          <td className="px-5 py-3">{b.hasNegativeBonus ? <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-600">Yes</span> : <span className="text-gray-300 text-xs">—</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
            </div>
            {buildersStats && <Pagination stats={buildersStats} onPageChange={setBuildersPage} />}
          </>
        )}
      </div>
      <style>{`@keyframes slideIn{from{transform:translateX(40px);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>
    </div>
  );
}

// ── FILTER CARD ────────────────────────────────────────────────────────────────

function FilterCard({
  search, dateFrom, dateTo, hasFilters, loading, pagination,
  onSearchChange, onSearchClear, onDateFromChange, onDateToChange, onClear,
}: {
  search: string;
  dateFrom: string;
  dateTo: string;
  hasFilters: boolean;
  loading: boolean;
  pagination: PaginationStats | null;
  onSearchChange: (v: string) => void;
  onSearchClear: () => void;
  onDateFromChange: (v: string) => void;
  onDateToChange: (v: string) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm mb-4 overflow-hidden">
      {/* Toggle header */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Filters</span>
          {hasFilters && (
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-primary-600 text-white text-[9px] font-bold">
              {[search, dateFrom, dateTo].filter(Boolean).length}
            </span>
          )}
        </div>
        <ChevronRightIcon
          className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${open ? "rotate-90" : ""}`}
        />
      </button>

      {/* Collapsible body */}
      {open && (
        <>
          <div className="px-4 pb-4 border-t border-gray-100">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-5 gap-y-4 pt-4">
              {/* File Name Search */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-gray-600">File Name</label>
                <div className="flex items-center gap-2 px-3 py-[9px] rounded-lg border border-gray-300 bg-white focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-100 transition-all">
                  <MagnifyingGlassIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <input
                    type="text"
                    placeholder="Search file name…"
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                    className="bg-transparent outline-none flex-1 min-w-0 text-sm text-gray-800 placeholder-gray-400"
                  />
                  {search && (
                    <button onClick={onSearchClear} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                      <XMarkIcon className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Date From */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-gray-600">Date From</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => onDateFromChange(e.target.value)}
                  className="w-full px-3 py-[9px] rounded-lg border border-gray-300 text-sm text-gray-800 bg-white outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                />
              </div>

              {/* Date To */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-gray-600">Date To</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => onDateToChange(e.target.value)}
                  className="w-full px-3 py-[9px] rounded-lg border border-gray-300 text-sm text-gray-800 bg-white outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                />
              </div>
            </div>
          </div>

          {/* Footer bar */}
          <div className="flex items-center gap-3 px-4 py-2.5 bg-gray-50 border-t border-gray-200">
            <button
              onClick={onClear}
              disabled={!hasFilters}
              className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Clear Filters
            </button>
            <span className="text-xs text-gray-500">
              {loading
                ? "Loading…"
                : pagination
                  ? `Showing ${pagination.totalRecords} file${pagination.totalRecords !== 1 ? "s" : ""}`
                  : "No results"}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

// ── MAIN COMPONENT ─────────────────────────────────────────────────────────────

const PayrollImports: React.FC = () => {
  const [files, setFiles] = useState<PayrollDataLoad[]>([]);
  const [pagination, setPagination] = useState<PaginationStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const handleSearchChange = (val: string) => {
    setSearch(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setDebouncedSearch(val); setPage(1); }, 350);
  };

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { page, pageSize: PAGE_SIZE };
      if (debouncedSearch) params.fileName = debouncedSearch;
      if (dateFrom) params.createdOnFrom = dateFrom;
      if (dateTo) params.createdOnTo = dateTo;
      const res = await AxiosInstance.get<ImportFilesResponse>("/api/payroll/import/files", { params });
      setFiles(res.data?.files ?? []);
      setPagination(res.data?.paginationStats ?? null);
    } catch (err) { console.error(err); setFiles([]); }
    finally { setLoading(false); }
  }, [page, debouncedSearch, dateFrom, dateTo]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const handleDownload = async (f: PayrollDataLoad, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (downloadingId === f.id) return;
    setDownloadingId(f.id);
    try {
      const res = await AxiosInstance.get(`/api/payroll/import/files/${f.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = f.fileName;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error("Download failed", err); }
    finally { setDownloadingId(null); }
  };

  const hasFilters = !!(debouncedSearch || dateFrom || dateTo);

  const clearFilters = () => {
    setSearch(""); setDebouncedSearch(""); setDateFrom(""); setDateTo(""); setPage(1);
  };

  return (
    <div className="min-h-full p-4">

      {/* ── Page header ── */}
      <div className="mb-5">
        <h1 className="text-lg font-semibold text-gray-900">Payroll Imports — Paid History</h1>
        <p className="text-xs text-gray-500 mt-0.5">
          Excel file uploaded by the Payroll team to confirm payment details — including payment date, payment reference, and any notes. <br/>Click a file name to download the file for details
        </p>
      </div>

      {/* ── Filter card — collapsible ── */}
      <FilterCard
        search={search}
        dateFrom={dateFrom}
        dateTo={dateTo}
        hasFilters={hasFilters}
        loading={loading}
        pagination={pagination}
        onSearchChange={handleSearchChange}
        onSearchClear={() => { setSearch(""); setDebouncedSearch(""); setPage(1); }}
        onDateFromChange={(v) => { setDateFrom(v); setPage(1); }}
        onDateToChange={(v) => { setDateTo(v); setPage(1); }}
        onClear={clearFilters}
      />

      {/* ── Table / Cards ── */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20"><CircularProgress /></div>
        ) : files.length === 0 ? (
          <div className="flex items-center justify-center py-20"><NoRows /></div>
        ) : (
          <>
            {/* ── Desktop table ── */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      File Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Uploaded By
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Uploaded On
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {files.map((f) => {
                    const isDownloading = downloadingId === f.id;
                    return (
                      <tr key={f.id} className="hover:bg-gray-50 group">
                        {/* File Name — click anywhere on cell to download */}
                        <td
                          className="px-6 py-4 cursor-pointer"
                          onClick={() => handleDownload(f)}
                        >
                          <Tooltip title="Click to download" placement="top-start">
                            <span className="font-medium text-xs text-gray-800 truncate max-w-[400px] block group-hover:text-primary-600 group-hover:underline underline-offset-2 transition-colors">
                              {f.fileName}
                            </span>
                          </Tooltip>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {f.createdBy?.split("@")[0] ?? "—"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {f.createdOn ? moment(f.createdOn).format("MM/DD/YYYY h:mm A") : "—"}
                        </td>
                        {/* Action — dedicated download icon, stops row propagation */}
                        <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Download file">
                            <button
                              onClick={(e) => handleDownload(f, e)}
                              disabled={isDownloading}
                              className="p-1.5 rounded-md border border-gray-300 text-gray-500 hover:text-primary-600 hover:border-primary-300 hover:bg-primary-50 transition-colors disabled:opacity-50"
                            >
                              {isDownloading
                                ? <CircularProgress size={14} />
                                : <ArrowDownTrayIcon className="w-4 h-4" />
                              }
                            </button>
                          </Tooltip>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* ── Mobile cards ── */}
            <div className="md:hidden">
              {files.map((f) => {
                const isDownloading = downloadingId === f.id;
                const bonusType = inferBonusType(f.fileName);
                const uploadedBy = f.createdBy?.split("@")[0] ?? "Unknown";
                const initial = uploadedBy[0]?.toUpperCase() ?? "?";

                return (
                  <div
                    key={f.id}
                    onClick={() => handleDownload(f)}
                    className={`relative flex gap-4 px-4 py-4 border-b border-gray-100 last:border-b-0 cursor-pointer transition-colors ${
                      isDownloading ? "bg-primary-50/60" : "hover:bg-gray-50 active:bg-gray-100"
                    }`}
                  >
                    {/* Left icon block */}
                    <div className="flex-shrink-0 flex items-start pt-0.5">
                      <div className="w-10 h-10 rounded-xl bg-primary-50 border border-primary-100 flex items-center justify-center">
                        {isDownloading
                          ? <CircularProgress size={18} />
                          : <DocumentArrowDownIcon className="w-5 h-5 text-primary-500" />
                        }
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      {/* Filename */}
                      <p className="text-[12px] font-semibold text-gray-900 leading-snug truncate mb-1.5">
                        {f.fileName}
                      </p>

                      {/* Tags row */}
                      <div className="flex items-center flex-wrap gap-1.5 mb-2">
                        {bonusType && (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
                            bonusType === "Closing" ? "bg-purple-100 text-purple-700" : "bg-emerald-100 text-emerald-700"
                          }`}>
                            {bonusType === "Stage5" ? "Stage 5" : "Closing"}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-gray-100 text-gray-500">
                          <CalendarDaysIcon className="w-2.5 h-2.5" />
                          {f.createdOn ? moment(f.createdOn).format("MMM D, YYYY") : "—"}
                        </span>
                      </div>

                      {/* Uploader row */}
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="w-5 h-5 rounded-full bg-gray-200 text-gray-600 text-[9px] font-bold flex items-center justify-center flex-shrink-0">
                            {initial}
                          </span>
                          <span className="text-[11px] text-gray-500 truncate">{uploadedBy}</span>
                        </div>
                        <span className={`flex-shrink-0 text-[10.5px] font-medium flex items-center gap-1 ${isDownloading ? "text-primary-500" : "text-gray-400"}`}>
                          <ArrowDownTrayIcon className="w-3 h-3" />
                          {isDownloading ? "Downloading…" : "Tap to download"}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {pagination && <Pagination stats={pagination} onPageChange={setPage} />}
      </div>
    </div>
  );
};

export default PayrollImports;